import {
  Form,
  Icon,
  Modal,
  ICONS,
  Button,
  TextInput,
  Typography,
  UploadInput,
  SelectInput,
  ButtonsWrapper,
  PLATFORM_THEME_COLORS,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';
import { useTranslation } from 'react-i18next';
import { DEFAULT_NAME_FIELD_REGEX } from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetDocumentLabelsByCompany } from 'src/hooks/documents-labels/get-documents-labels.hook';
import { useCreateDocumentTemplate } from 'src/hooks/documents-templates/create-document-template.hook';

import { DraggerContent } from './upload-template-modal.styles';

const UploadTemplateModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(['common', 'settings']);
  const currentCompanyId = useGetCurrentCompanyId();
  const { loading, documentsLabels } = useGetDocumentLabelsByCompany(currentCompanyId);
  const { createDocumentTemplate, loading: createLoading } = useCreateDocumentTemplate({
    onSuccess: onClose,
  });

  return (
    <Modal isOpened onClose={onClose} title={t('settings:documents.buttons.uploadNewTemplate')}>
      <Form
        style={{ position: 'relative' }}
        layout="vertical"
        onFinish={({ title, documentLabelId, template }) => {
          const [{ originFileObj: file }] = template.fileList;
          const payload = { file, title, documentLabelId, companyId: currentCompanyId };

          createDocumentTemplate({ variables: { payload } });
        }}
      >
        {createLoading && <Loader position="absolute" />}
        <Form.Item
          name="title"
          label={t('settings:permissionGroupSettings.labels.title')}
          rules={[
            { required: true, message: t('settings:permissionGroupSettings.errors.titleRequired') },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <TextInput maxLength={50} placeholder={t('settings:documents.labels.headline')} />
        </Form.Item>
        <Form.Item
          name="documentLabelId"
          label={t('common:labels.label')}
          rules={[
            { required: true, message: t('common:validation.error.labelRequired') },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <SelectInput loading={loading} placeholder={t('settings:documents.labels.selectLabel')}>
            {documentsLabels.map(({ _id: documentLabelId, name }: any) => (
              <SelectInput.Option key={documentLabelId} value={documentLabelId}>
                {name}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name="template"
          label={t('settings:documents.labels.importTemplate')}
          rules={[
            { required: true, message: t('settings:documents.errors.templateRequired') },
            () => ({
              validator(_, { file: { name, size } }) {
                const extension = name.split('.').pop();

                if (!/doc*/.test(extension)) {
                  return Promise.reject(new Error(t('settings:documents.errors.wrongFileFormat')));
                }

                if (size > 1000 * 1000 * 20) {
                  return Promise.reject(
                    new Error(t('settings:documents.errors.wrongFileSize', { detail: 20 }))
                  );
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <UploadInput.Dragger accept=".doc, .docx" maxCount={1} beforeUpload={() => false}>
            <DraggerContent>
              <Icon
                icon={ICONS.inbox}
                color={PLATFORM_THEME_COLORS.primary.blue}
                size={26}
                rightMargin={false}
              />
              <Typography>{t('settings:documents.labels.dragBoxText')}</Typography>
            </DraggerContent>
          </UploadInput.Dragger>
        </Form.Item>
        <ButtonsWrapper>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};

export default UploadTemplateModal;
